import * as BlocklyCore from 'blockly/core';
import _ from 'lodash';

import {SOUND_PREFIX} from '@cdo/apps/assetManagement/assetPrefix';
import localization from '@cdo/apps/localization';
import {EVENTS} from '@cdo/apps/metrics/AnalyticsConstants';
import analyticsReporter from '@cdo/apps/metrics/AnalyticsReporter';
import {MetricEvent} from '@cdo/apps/metrics/events';
import MetricsReporter from '@cdo/apps/metrics/MetricsReporter';
import {getStore} from '@cdo/apps/redux';
import {setFailedToGenerateCode} from '@cdo/apps/redux/blockly';

import * as BlockUtils from '../block_utils';
import UserPreferences from '../lib/util/UserPreferences';
import {shrinkBlockSpaceContainer} from '../templates/instructions/utils';

import {
  DARK_THEME_SUFFIX,
  Themes,
  BLOCK_TYPES,
  BLOCKLY_THEME,
} from './constants';
import {
  BlockJson,
  BlocklyWrapperType,
  ExtendedBlock,
  ExtendedWorkspaceSvg,
  JsonBlockConfig,
  WorkspaceSerialization,
} from './types';

type xmlAttribute = string | null;
type InputTuple = [string, string, number];
type InputCallback = () => void;
type InputArgs = [...(InputTuple | InputCallback)[], number];
type BlockList = Array<BlocklyCore.Block | BlocklyCore.BlockSvg>;

// Considers an attribute true only if it is explicitly set to 'true' (i.e. defaults to false if unset).
export const FALSEY_DEFAULT = (attributeValue: xmlAttribute) =>
  attributeValue === 'true';

// Considers an attribute true unless it is explicitly set to 'false' (i.e. defaults to true if unset).
export const TRUTHY_DEFAULT = (attributeValue: xmlAttribute) =>
  attributeValue !== 'false';

/**
 * Reads a boolean attribute from an XML element and determines its value based on a callback function.
 * The callback function determines how we interpret the attribute value as a boolean.
 * @param {Element} xmlElement - The XML element from which to read the attribute.
 * @param {string} attribute - The name of the attribute to read from the XML element.
 * @param {function(string): boolean} [callback=FALSEY_DEFAULT] - A callback function that takes the attribute value as a string and returns a boolean.
 * @returns {boolean} The boolean value of the attribute as determined by the callback function.
 */
export function readBooleanAttribute(
  xmlElement: Element,
  attribute: string,
  callback: (attributeValue: xmlAttribute) => boolean = FALSEY_DEFAULT
) {
  const attributeValue = xmlElement.getAttribute(attribute);
  return callback(attributeValue);
}

export function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function parseSoundPathString(text: string) {
  // Example string paths:
  // 'sound://category_board_games/card_dealing_multiple.mp3'
  // 'sound://default.mp3'
  if (!text.startsWith(SOUND_PREFIX) || !text.endsWith('.mp3')) {
    throw new Error('This is not a valid path to a sound file.');
  }
  const pathStringArray = text.split('/');
  let category = '';
  // Some sounds do not include a category, such as default.mp3
  if (pathStringArray[2].includes('category_')) {
    category = pathStringArray[2];
    // Example: 'category_board_games' becomes 'Board games: '
    category = category.replace('category_', '');
    category = category.replace(/_/g, ' ');
    category = capitalizeFirstLetter(category) + ': ';
  }
  // Example: 'card_dealing_multiple.mp3' becomes 'card_dealing_multiple'
  const soundName = pathStringArray[pathStringArray.length - 1].replace(
    '.mp3',
    ''
  );
  // Examples: 'Board games: card_dealing_multiple', 'default'
  const fieldText = `${category}${soundName}`;
  return fieldText;
}

/**
 * Converts a printer-style string range to an array of numbers
 * e.g., "1,2,4-6" becomes [1,2,4,5,6]
 * @param rangeString {string} printer-style range, e.g., "1,2,4-6"
 * @returns  array of numbers
 */
export function printerStyleNumberRangeToList(rangeString: string) {
  const rangeStringNoSpaces = rangeString.replace(/ /g, '');
  const rangeItems = rangeStringNoSpaces.split(',');
  const rangeRegExp = /^(\d+)-(\d+)$/; // e.g., "4-6"
  const numberRegExp = /^(\d+)$/; // e.g., "1", "2"
  const fullNumberList = rangeItems.reduce<number[]>((numberArray, currExp) => {
    const rangeResult = rangeRegExp.exec(currExp);
    const numberResult = numberRegExp.exec(currExp);
    if (rangeResult) {
      const lowerBound = Number(rangeResult[1]);
      const upperNonInclusiveBound = Number(rangeResult[2]) + 1;
      const rangeArray = _.range(lowerBound, upperNonInclusiveBound);
      numberArray = numberArray.concat(rangeArray);
    } else if (numberResult) {
      numberArray.push(Number(numberResult[1]));
    }
    return numberArray;
  }, []);
  return fullNumberList;
}

/**
 * Converts an array of numbers to a string of numbers separated by commas
 * e.g., [1,2,4,5,6] becomes "1,2,4,5,6", [5] becomes "5"
 * @param numberList array of numbers
 * @returns  numberString {string}
 */
export function numberListToString(numberList: number[]) {
  let numberString = numberList.reduce<string>((str, curr) => {
    str = str + curr + ',';
    return str;
  }, '');
  if (numberList.length > 0) {
    numberString = numberString.slice(0, -1);
  }
  return numberString;
}

/**
 * Determines whether the hidden procedure definition workspace should be skipped during serialization.
 * The hidden workspace is a counter-part to the main workspace containing blocks for functions and behaviors.
 *
 * @param {Blockly.WorkspaceSvg} workspace - The workspace to be checked for serialization as hidden.
 * @returns {boolean} Returns `true` if the hidden workspace should be skipped, otherwise `false`.
 */
export function shouldSkipHiddenWorkspace(workspace: BlocklyCore.WorkspaceSvg) {
  return (
    !Blockly.getHiddenDefinitionWorkspace ||
    Blockly.getMainWorkspace().id !== workspace.id ||
    Blockly.isToolboxMode
  );
}

/**
 * Handle a failure to get workspace code by Blockly by updating the
 * redux store and logging the error.
 * We only want to log the error once per failure since getWorkspaceCode
 * gets called many times and the error will be the same every time.
 * @param {MetricEvent} eventName Event name to log
 * @param {Error} error Error thrown by getWorkspaceCode
 */
export function handleCodeGenerationFailure(
  eventName: MetricEvent,
  error: Error
) {
  const store = getStore();
  if (!store.getState().blockly.failedToGenerateCode) {
    store.dispatch(setFailedToGenerateCode(true));
    MetricsReporter.logError({
      event: eventName,
      errorMessage: error.message,
      stackTrace: error.stack,
    });
  }
}

// Returns the current theme name without the 'dark' suffix, if present.
export function getBaseName(themeName: Themes) {
  if (themeName) {
    return themeName.replace(DARK_THEME_SUFFIX, '');
  }
}

export function isDarkTheme(theme: BlocklyCore.Theme | undefined) {
  return theme?.name.includes(DARK_THEME_SUFFIX);
}

export const INFINITE_LOOP_TRAP =
  '  executionInfo.checkTimeout(); if (executionInfo.isTerminated()){return;}\n';

export const LOOP_HIGHLIGHT = 'loopHighlight();\n';
const LOOP_HIGHLIGHT_RE = new RegExp(
  LOOP_HIGHLIGHT.replace(/\(.*\)/, '\\(.*\\)') + '\\s*',
  'g'
);

function escapeRegExp(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Extract the user's code as raw JavaScript.
 * @param {string} code Generated code.
 * @return {string} The code without serial numbers and timeout checks.
 */
export function strip(code: string) {
  return (
    code
      // Strip out serial numbers.
      .replace(/(,\s*)?'block_id_[^']+'\)/g, ')')
      // Remove timeouts.
      .replace(new RegExp(escapeRegExp(INFINITE_LOOP_TRAP), 'g'), '')
      // Strip out loop highlight
      .replace(LOOP_HIGHLIGHT_RE, '')
      // Strip out class namespaces.
      .replace(/(StudioApp|Maze|Turtle)\./g, '')
      // Strip out particular helper functions.
      .replace(/^function (colour_random)[\s\S]*?^}/gm, '')
      // Collapse consecutive blank lines.
      .replace(/\n\n+/gm, '\n\n')
      // Trim.
      .replace(/^\s+|\s+$/g, '')
  );
}

/**
 * Interpolate a message string, creating fields and inputs.
 * @param {string} msg The message string to parse.  %1, %2, etc. are symbols
 *     for value inputs.
 * @param {!Array.<string|number>|number} inputArgs A series of tuples or
 *     callbacks that each specify the value inputs to create.  If a callback
 *     is provided, we defer rendering to that method. Otherwise, each tuple has
 *     three values:
 *       the input name
 *       its check type
 *       its field's alignment.
 *     The last parameter is not a tuple, but just an alignment for any trailing
 *     dummy input.  This last parameter is mandatory; there may be any number
 *     of tuples (though the number of tuples must match the symbols in msg).
 * This was copied and modernized from the original CDO Blockly version:
 * https://github.com/code-dot-org/blockly/blob/v4.1.0/core/ui/block.js#L2632-L2692
 */
export function interpolateMsg(
  this: ExtendedBlock,
  msg: string,
  ...inputArgs: InputArgs
): void {
  // Ensure msg is a string.
  if (typeof msg !== 'string') {
    throw new Error('Assertion failed: msg is not a string');
  }
  // Remove the dummy alignment from the end of args.
  const dummyAlign = inputArgs.pop();
  if (typeof dummyAlign !== 'number') {
    throw new Error('Assertion failed: dummyAlign is not a number');
  }

  const tokens = msg.split(/(%\d)/);
  const usedArgs: boolean[] = new Array(inputArgs.length).fill(false);
  for (let i = 0; i < tokens.length; i += 2) {
    const text = tokens[i].trim();
    const symbol = tokens[i + 1];
    if (symbol) {
      // Value input.
      const digit = parseInt(symbol.charAt(1), 10);
      const inputArg = inputArgs[digit - 1];

      if (typeof inputArg === 'function') {
        this.appendDummyInput().appendField(text);
        inputArg();
      } else if (Array.isArray(inputArg)) {
        this.appendValueInput(inputArg[0])
          .setCheck(inputArg[1])
          .setAlign(inputArg[2])
          .appendField(text);
      }
      usedArgs[digit - 1] = true; // Mark input as used.
    } else if (text) {
      // Trailing dummy input.
      this.appendDummyInput().setAlign(dummyAlign).appendField(text);
    }
  }

  // Verify that all inputs were used.
  const unusedArgIndex = usedArgs.findIndex(used => !used);
  if (unusedArgIndex !== -1) {
    const formattedMessage = `Input "${
      unusedArgIndex + 1
    }" not used in message: "${msg}"`;
    throw new Error(formattedMessage);
  }

  // Make the inputs inline unless there is only one input and no text follows it.
  this.setInputsInline(!msg.match(/%1\s*$/));
}

/**
 * Retrieves the top-level Blockly blocks from the students Blockly workspace and
 * potentially includes the top-level blocks from the hidden definition workspace.
 *
 * @returns {BlockList} An array of the top-level blocks.
 */
export function getCodeBlocks(): BlockList {
  let codeBlocks: BlockList = [];
  let hiddenBlocks: BlockList = [];
  const mainBlocks = Blockly.mainBlockSpace.getTopBlocks(true) as BlockList;

  // The hidden workspace is only present in Blockly labs where the modal
  // function editor is enabled.
  if (Blockly.getHiddenDefinitionWorkspace()) {
    hiddenBlocks = Blockly.getHiddenDefinitionWorkspace().getTopBlocks(
      true
    ) as BlockList;
  }

  // Hidden blocks need to be listed first in case they would set the
  // value of global variables.
  codeBlocks = [...hiddenBlocks, ...mainBlocks];

  return codeBlocks;
}

/**
 * Retrieves all Blockly blocks from the student's Blockly workspaces.
 * This is useful for providing the student with feedback about the total
 * number of blocks they have used or added.
 *
 * @returns {BlockList} An array of all blocks.
 */
export function getAllBlocks(): BlockList {
  return [
    ...Blockly.mainBlockSpace.getAllUsedBlocks(),
    ...(Blockly.getHiddenDefinitionWorkspace()
      ? Blockly.getHiddenDefinitionWorkspace().getAllBlocks()
      : []),
  ];
}

export function disableOrphanBlocks(eventWorkspace: BlocklyCore.Workspace) {
  // When a function definition is moved, we should not suddenly enable
  // its call blocks.
  eventWorkspace.getTopBlocks().forEach(block => {
    if (block.type === BLOCK_TYPES.procedureCall) {
      block.setDisabledReason(true, 'ORPHANED');
    }
    updateBlockEnabled(block);
  });
}

export function updateBlockEnabled(block: BlocklyCore.Block) {
  // Changing blocks as part of this event shouldn't be undoable.
  const initialUndoFlag = Blockly.Events.getRecordUndo();
  try {
    Blockly.Events.setRecordUndo(false);
    const parent = block.getParent();
    if (parent && parent.isEnabled()) {
      const children = block.getDescendants(false);
      for (let i = 0, child; (child = children[i]); i++) {
        child.setDisabledReason(false, 'ORPHANED');
      }
    } else if (block.outputConnection || block.previousConnection) {
      let currentBlock: BlocklyCore.Block | null = block;
      do {
        currentBlock.setDisabledReason(true, 'ORPHANED');
        currentBlock = currentBlock.getNextBlock();
      } while (currentBlock);
    }
  } finally {
    Blockly.Events.setRecordUndo(initialUndoFlag);
  }
}

/**
 * Sets the theme for the workspace and re-renders blocks if the font size changed.
 *
 * @param {BlocklyCore.Workspace} workspace - The Blockly workspace to set the theme for.
 * @param {BlocklyCore.Theme} theme - The theme to apply to the workspace.
 */
export function setThemeAndRenderBlocks(
  workspace: BlocklyCore.WorkspaceSvg,
  theme: BlocklyCore.Theme,
  previousTheme?: BlocklyCore.Theme
) {
  if (theme && workspace?.rendered) {
    // Update the main workspace's flyout if it exists.
    if (workspace.getFlyout()) {
      setThemeAndRenderBlocks(
        workspace.getFlyout()!.getWorkspace(),
        theme,
        previousTheme
      );
    }
    workspace.setTheme(theme);
    // Re-render blocks if the font size changed.
    // Once https://github.com/google/blockly/issues/7782 is resolved,
    // we should be able to remove this.
    if (theme.fontStyle?.size !== previousTheme?.fontStyle?.size) {
      workspace.getAllBlocks().map(block => {
        block.markDirty();
        block.render();
      });
      // If this is an embedded workspace, we resize its container to avoid cropping or excess padding.
      if (Blockly.embeddedWorkspaces.includes(workspace.id)) {
        shrinkBlockSpaceContainer(workspace, true);
      }
      // Adjust the width of the vertical flyout if it exists.
      if (workspace.getFlyout()) {
        workspace.getFlyout()!.reflow();
      }
    }
  }
}

export function setWorkspaceTheme(name: string) {
  // Save the theme to user preferences, falling back to localStorage for signed-out users.
  new UserPreferences().setBlocklyTheme(name, () =>
    localStorage.setItem(BLOCKLY_THEME, name)
  );

  const analyticsData = Blockly.analyticsData;
  analyticsReporter.sendEvent(EVENTS.BLOCKLY_LAB_SETTING_CHANGED, {
    setting: EVENTS.BLOCKLY_SETTING_THEME,
    value: name,
    ...analyticsData,
  });

  const currentTheme = Blockly.getMainWorkspace().getTheme();
  const themeName = name + (isDarkTheme(currentTheme) ? DARK_THEME_SUFFIX : '');
  setAllWorkspacesTheme(Blockly.themes[themeName as Themes], currentTheme);
}

export function setAllWorkspacesTheme(
  newTheme: BlocklyCore.Theme,
  previousTheme: BlocklyCore.Theme | undefined
) {
  Blockly.Workspace.getAll().forEach(baseWorkspace => {
    // Headless workspaces do not have the ability to set the theme.
    const workspace = baseWorkspace as BlocklyCore.WorkspaceSvg;
    setThemeAndRenderBlocks(workspace, newTheme, previousTheme);
  });
}

/**
 * Adds a warning to blocks that are not positioned under a static category block,
 * except when there are no categories at all. If warnings are ignored, we will
 * still save the blocks into a "DEFAULT" category.
 */
export function validateBlockCategories(workspace: BlocklyCore.WorkspaceSvg) {
  const topBlocks = workspace.getTopBlocks(true);

  const noCategoryBlocks =
    !workspace.getBlocksByType(BLOCK_TYPES.category).length &&
    !workspace.getBlocksByType(BLOCK_TYPES.categoryDynamic).length;

  let currentCategoryBlock: BlocklyCore.BlockSvg | null = null;
  let warningText = 'This block is not positioned under a category.';

  topBlocks.forEach(block => {
    // If there are no categories, remove all warnings.
    if (noCategoryBlocks) {
      block.setWarningText(null);
      return;
    }
    if (block.type === BLOCK_TYPES.category) {
      // Update the current category to this block
      currentCategoryBlock = block;
    } else if (block.type === BLOCK_TYPES.categoryDynamic) {
      // Reset the current category since dynamic categories can't include static blocks
      currentCategoryBlock = null;
      warningText = 'Auto-populated categories cannot include static blocks.';
    } else {
      // All non-category blocks
      if (!currentCategoryBlock) {
        // No static category block above this block
        block.setWarningText(warningText);
      } else {
        // Valid placement under a static category block
        block.setWarningText(null);
      }
    }
  });
}

export function applyBlockIdOverrides(
  workspaceJson: WorkspaceSerialization,
  overrides: BlocklyWrapperType['blockIdOverrides']
) {
  function walkBlocks(block: JsonBlockConfig) {
    if (block.id && overrides[block.id]) {
      block.id = overrides[block.id];
    }
    if (block.next?.block) {
      walkBlocks(block.next.block);
    }
    if (block.inputs) {
      for (const stmt of Object.values(block.inputs)) {
        if (stmt?.block) {
          walkBlocks(stmt.block);
        }
      }
    }
  }

  if (Array.isArray(workspaceJson.blocks?.blocks)) {
    workspaceJson.blocks.blocks.forEach(walkBlocks);
  }
}

/**
 * Redraws a workspace.
 */
export function refreshWorkspace(workspace: ExtendedWorkspaceSvg) {
  const state = Blockly.serialization.workspaces.save(workspace);
  // Do not allow the variables to be redefined as they will conflict when the
  // block data is reloaded.
  const variables = workspace.globalVariables;
  if (variables) {
    workspace.globalVariables = [];
  }
  Blockly.serialization.workspaces.load(state, workspace);
  if (variables) {
    workspace.globalVariables = variables;
  }

  // Handle the toolbox
  const toolbox = workspace.getToolbox();
  if (toolbox) {
    // Close the toolbox when it exists
    toolbox.clearSelection();
  }

  // Handle the flyout as well
  const flyout = workspace.getFlyout(true);
  if (flyout) {
    // Redraw the flyout
    if (workspace.options.languageTree) {
      flyout.show(workspace.options.languageTree);
    }

    // Scroll the flyout to the start
    if (typeof flyout.scrollToStart === 'function') {
      flyout.scrollToStart();
    }
  }
}

/**
 * Reverts localized variables in a workspace to their source (English) forms.
 */
export function unlocalizeVariables(workspace: ExtendedWorkspaceSvg) {
  // Go through the original variables and return them to their source language.
  for (const variable of workspace.getAllVariables()) {
    if (Blockly.SourceVariables[variable.getId()]) {
      workspace.renameVariableById(
        variable.getId(),
        Blockly.SourceVariables[variable.getId()]
      );
    }
  }
}

export function initializeVariableLocalization(
  workspace: ExtendedWorkspaceSvg
) {
  // Clear known variables
  Blockly.SourceVariables = {};
  workspace.sourceGlobalVariables = [];

  const flyout = workspace.getFlyout(true);
  if (flyout) {
    // For the flyout, we have to find all of the variables in the input list
    // for each block in the flyout. Blockly does not give us a convenient way
    // to do this, but this is modelled after the block methods to pull out
    // all of the variable ids in the block (Block.getVars())
    const flyoutWorkspace = flyout.getWorkspace();
    if (flyoutWorkspace) {
      for (const block of flyoutWorkspace.getTopBlocks()) {
        for (const input of block?.inputList || []) {
          for (const field of input?.fieldRow || []) {
            if (field?.referencesVariables()) {
              const varField = field as BlocklyCore.FieldVariable;
              const variable = varField?.getVariable();
              if (variable) {
                const id = variable.getId();
                const oldName = variable.getName();
                Blockly.SourceVariables[id] = oldName;

                // And we note them as 'global' variables
                if (!workspace.sourceGlobalVariables.includes(oldName)) {
                  workspace.sourceGlobalVariables.push(oldName);
                }
              }
            }
          }
        }
      }
    }
  }

  // We keep track of all variables in the workspace
  for (const variable of workspace.getAllVariables()) {
    const id = variable.getId();
    const oldName = variable.getName();
    Blockly.SourceVariables[id] = oldName;

    // And we note them as 'global' variables
    if (!workspace.sourceGlobalVariables.includes(oldName)) {
      workspace.sourceGlobalVariables.push(oldName);
    }
  }

  // Keep track of the installed global variables, too
  if (workspace.globalVariables) {
    workspace.sourceGlobalVariables = workspace.sourceGlobalVariables.concat(
      workspace.globalVariables
    );
  }
}

/**
 * Goes through workspace variables and localizes them.
 */
export function localizeVariables(workspace: ExtendedWorkspaceSvg) {
  // Localize the flyout, if there is one
  const languageTree = workspace.options.languageTree;
  if (languageTree) {
    // Update the language tree that is used to represent the flyout blocks
    for (const [id, oldName] of Object.entries(Blockly.SourceVariables)) {
      let newName: string = localization.translate(`[variable] ${oldName}`, [
        'blockly-variable',
        'blockly-block',
      ]);
      if (newName.startsWith('[variable] ')) {
        newName = newName.substring(11);
      } else {
        console.error(
          'Global variable translation does not have the [variable] tag',
          oldName,
          newName
        );
        newName = oldName;
      }

      for (const node of languageTree?.contents || []) {
        const blockNode = node as BlocklyCore.utils.toolbox.BlockInfo;
        const xml = blockNode.blockxml;
        if (xml !== undefined && typeof xml !== 'string') {
          const node = xml as HTMLElement;
          for (const el of Array.from(
            node.querySelectorAll(`field[id="${id}"]`) || []
          )) {
            const fieldElement = el as HTMLElement;
            fieldElement.textContent = newName;
          }
        }
      }
    }
  }

  for (const variable of workspace.getAllVariables()) {
    Blockly.SourceVariables[variable.getId()] ||= variable.getName();
    const oldName = Blockly.SourceVariables[variable.getId()];

    // Make sure we've seen the old name before
    if (workspace.sourceGlobalVariables?.includes(oldName)) {
      const globalVariableIndex =
        workspace.sourceGlobalVariables.indexOf(oldName);
      let newName: string = localization.translate(`[variable] ${oldName}`, [
        'blockly-variable',
        'blockly-block',
      ]);
      if (newName.startsWith('[variable] ')) {
        newName = newName.substring(11);
      } else {
        console.error(
          'Global variable translation does not have the [variable] tag',
          oldName,
          newName
        );
        newName = oldName;
      }

      workspace.renameVariableById(variable.getId(), newName);
      if (workspace.globalVariables?.[globalVariableIndex]) {
        workspace.globalVariables[globalVariableIndex] = newName;
      }
    }
  }
}

/**
 * Localizes a modern block definition and returns the localized form of it.
 */
export function localizeBlockDefinition(blockDefinition: BlockJson): BlockJson {
  return {
    ...blockDefinition,
    tooltip:
      blockDefinition.tooltip !== undefined
        ? localization.translate(blockDefinition.tooltip)
        : undefined,
    message0:
      blockDefinition.message0 !== undefined
        ? localization.translate(blockDefinition.message0, ['blockly-block'])
        : undefined,
    message1:
      blockDefinition.message1 !== undefined
        ? localization.translate(blockDefinition.message1, ['blockly-block'])
        : undefined,
    message2:
      blockDefinition.message2 !== undefined
        ? localization.translate(blockDefinition.message2, ['blockly-block'])
        : undefined,
    message3:
      blockDefinition.message3 !== undefined
        ? localization.translate(blockDefinition.message3, ['blockly-block'])
        : undefined,
  };
}

/**
 * Updates the locale and localization strings for all workspaces.
 */
export function updateLocale(rtl: boolean) {
  // Call into our localization engine to get the new blocks and refresh all active
  // workspaces.

  // Copy over new localization keys for the normal blocks in 'Msg'
  for (const [key, value] of Object.entries(Blockly.Msg || {})) {
    Blockly.SourceMsg[key] ||= value;
    Blockly.Msg[key] = localization.translate(Blockly.SourceMsg[key], [
      'blockly-block',
    ]);
  }

  // Go through custom and shared blocks to translate the blockText there
  // This means recreating the block init() functions with updated block
  // configurations.
  for (const blockName of Object.keys(
    Blockly.SourceCustomBlocks.blockDefinitionsByName
  )) {
    const blockDefinition =
      Blockly.SourceCustomBlocks.blockDefinitionsByName[blockName];
    Blockly.SourceCustomBlocks.blockTexts[blockName] ||=
      blockDefinition.config.blockText;
    const oldBlockText = Blockly.SourceCustomBlocks.blockTexts[blockName];
    let newBlockText: string = oldBlockText;
    if (blockDefinition.config.returnType === 'Behavior') {
      newBlockText = localization.translate(`[behavior] ${oldBlockText}`, [
        'blockly-block',
        'blockly-behavior',
      ]);
      if (newBlockText.startsWith('[behavior] ')) {
        newBlockText = newBlockText.substring(11);
      } else {
        console.error(
          'Behavior translation does not have the [behavior] tag',
          oldBlockText,
          newBlockText
        );
      }
    } else {
      newBlockText = localization.translate(oldBlockText, ['blockly-block']);
    }

    // Unfreeze the block definition to add the new translation strings
    Blockly.SourceCustomBlocks.blockDefinitionsByName[blockName] = {
      ...blockDefinition,
      config: {
        ...blockDefinition.config,
        blockText: newBlockText,
      },
    };
  }

  const blockDefinitions = Object.values(
    Blockly.SourceCustomBlocks.blockDefinitionsByName
  );

  BlockUtils.installCustomBlocks({
    blockly: Blockly,
    blockDefinitions,
    customInputTypes: Blockly.SourceCustomInputTypes,
  });

  const mainWorkspace = Blockly.getMainWorkspace();
  if (mainWorkspace) {
    mainWorkspace.RTL = rtl;
    localizeVariables(mainWorkspace as ExtendedWorkspaceSvg);
    refreshWorkspace(mainWorkspace as ExtendedWorkspaceSvg);
  }

  // Refresh embedded workspaces (blocks in instructions, documentation, etc)
  Blockly.embeddedWorkspaces.forEach(workspace_id => {
    const workspace = Blockly.Workspace.getById(
      workspace_id
    ) as ExtendedWorkspaceSvg;
    if (workspace) {
      workspace.RTL = rtl;
      localizeVariables(workspace as ExtendedWorkspaceSvg);
      refreshWorkspace(workspace);
    }
  });
}
